import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import {
  LogOut, Eye, MousePointerClick, Users, TrendingUp, Loader2,
  Inbox, FileText, BarChart3, Download, Check, Trash2, Plus, Edit, Package, Upload, ShoppingBag,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#C89D3C", "#3B2A1A", "#8A1A2B", "#1D524D", "#1B2A49", "#E06B22"];

function StatCard({ icon: Icon, label, value, testId, accent = "#C89D3C" }) {
  return (
    <div className="bg-white border border-[#3B2A1A]/10 p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="overline text-[10px]">{label}</p>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <p className="font-serif-display text-4xl text-[#3B2A1A] mt-3">{value}</p>
    </div>
  );
}

// ---------- LOGIN ----------
function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("sd_admin_token", data.access_token);
      onSuccess(data.access_token);
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invalid credentials");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#FBF6EC] flex items-center justify-center p-6 mandala-bg">
      <div className="w-full max-w-md bg-white border border-[#3B2A1A]/10 p-10">
        <div className="flex justify-center mb-8"><Logo /></div>
        <p className="overline text-center">Admin Access</p>
        <h1 className="font-serif-display text-3xl text-center text-[#3B2A1A] mt-2">Sanatan Dharma Panel</h1>
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Email</label>
            <input required type="email" data-testid="admin-input-email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Password</label>
            <input required type="password" data-testid="admin-input-password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
          </div>
          {err && <p className="text-sm text-[#8A1A2B]" data-testid="admin-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="admin-submit-btn" className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- ANALYTICS TAB ----------
function AnalyticsTab({ token, onExpire }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/summary?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((e) => { if (e.response?.status === 401) onExpire(); })
      .finally(() => setLoading(false));
  }, [days, token, onExpire]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 size={22} className="animate-spin text-[#C89D3C]" /></div>;
  if (!data) return <p className="text-[#8A1A2B]">Could not load analytics.</p>;

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline">Overview</p>
          <h2 className="font-serif-display text-3xl text-[#3B2A1A] mt-1">Traffic — last {days === 365 ? "1 year" : `${days} days`}</h2>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="admin-days-select"
          className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] bg-transparent border border-[#3B2A1A]/20 px-4 py-2 focus:outline-none">
          <option value={7}>Last 7 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={90}>Last 90 Days</option>
          <option value={365}>Last 1 Year</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard icon={Eye} label="Page Views" value={data.total_page_views.toLocaleString()} testId="stat-views" />
        <StatCard icon={Users} label="Unique Sessions" value={data.unique_sessions.toLocaleString()} testId="stat-sessions" />
        <StatCard icon={MousePointerClick} label="CTA Clicks" value={data.total_clicks.toLocaleString()} testId="stat-clicks" />
        <StatCard icon={TrendingUp} label="Avg Views/Session" value={data.avg_views_per_session} testId="stat-avg" />
      </div>

      <section className="bg-white border border-[#3B2A1A]/10 p-6">
        <p className="overline mb-4">Daily Traffic</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily_series}>
              <CartesianGrid stroke="#3B2A1A" strokeOpacity={0.06} vertical={false} />
              <XAxis dataKey="date" stroke="#8A7969" tick={{ fontSize: 11 }} />
              <YAxis stroke="#8A7969" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#FBF6EC", border: "1px solid #3B2A1A22" }} />
              <Line type="monotone" dataKey="views" stroke="#C89D3C" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-white border border-[#3B2A1A]/10 p-6">
        <p className="overline mb-4">Traffic by Hour of Day</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.hourly_series}>
              <CartesianGrid stroke="#3B2A1A" strokeOpacity={0.06} vertical={false} />
              <XAxis dataKey="hour" stroke="#8A7969" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} />
              <YAxis stroke="#8A7969" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#FBF6EC", border: "1px solid #3B2A1A22" }} />
              <Bar dataKey="views" fill="#C89D3C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="top-pages">
          <p className="overline mb-4">Top Pages</p>
          <ul className="divide-y divide-[#3B2A1A]/10">
            {data.top_pages.length === 0 && <li className="text-sm text-[#8A7969] py-3 font-light">No page views yet.</li>}
            {data.top_pages.map((p) => (
              <li key={p.path} className="flex justify-between items-center py-3">
                <span className="text-sm text-[#3B2A1A] truncate max-w-[70%]">{p.path}</span>
                <span className="font-serif-display text-lg text-[#C89D3C]">{p.views}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="top-referrers">
          <p className="overline mb-4">Top Referrers</p>
          <ul className="divide-y divide-[#3B2A1A]/10">
            {data.top_referrers.length === 0 && <li className="text-sm text-[#8A7969] py-3 font-light">No traffic yet.</li>}
            {data.top_referrers.map((r) => (
              <li key={r.source} className="flex justify-between items-center py-3">
                <span className="text-sm text-[#3B2A1A] truncate max-w-[70%]">{r.source || "Direct"}</span>
                <span className="font-serif-display text-lg text-[#C89D3C]">{r.visits}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="devices-chart">
          <p className="overline mb-4">Devices</p>
          {data.devices.length === 0 ? <p className="text-sm text-[#8A7969] font-light">No data.</p> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.devices} dataKey="views" nameKey="device" cx="50%" cy="50%" outerRadius={80}>
                    {data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#FBF6EC", border: "1px solid #3B2A1A22" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
        <section className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="top-clicks">
          <p className="overline mb-4">Top Clicked CTAs</p>
          {data.top_clicks.length === 0 ? <p className="text-sm text-[#8A7969] font-light">No CTA clicks tracked yet.</p> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_clicks} layout="vertical">
                  <CartesianGrid stroke="#3B2A1A" strokeOpacity={0.06} horizontal={false} />
                  <XAxis type="number" stroke="#8A7969" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" stroke="#8A7969" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip contentStyle={{ background: "#FBF6EC", border: "1px solid #3B2A1A22" }} />
                  <Bar dataKey="count" fill="#C89D3C" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      <section className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="recent-activity">
        <p className="overline mb-4">Recent Activity — last 20 events</p>
        <ul className="divide-y divide-[#3B2A1A]/10 text-sm">
          {(data.recent_activity || []).length === 0 && <li className="text-[#8A7969] py-3 font-light">No activity yet.</li>}
          {(data.recent_activity || []).map((e, i) => (
            <li key={i} className="py-3 flex items-center justify-between gap-4">
              <span className="text-xs uppercase tracking-widest text-[#8A7969]">{e.type}</span>
              <span className="flex-1 truncate text-[#3B2A1A]">{e.path}{e.label ? ` · ${e.label}` : ""}</span>
              <span className="text-xs text-[#8A7969]">{e.ts?.slice(11,16)} UTC</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// ---------- LEADS TAB ----------
function LeadsTab({ token, onExpire }) {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState({ kind: "", status: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("new");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.kind) q.set("kind", filter.kind);
      if (filter.status) q.set("status", filter.status);
      const [ls, s] = await Promise.all([
        axios.get(`${API}/admin/leads?${q.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/leads/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLeads(ls.data.leads); setSummary(s.data);
    } catch (e) {
      if (e.response?.status === 401) onExpire();
    } finally { setLoading(false); }
  }, [filter, token, onExpire]);

  useEffect(() => { load(); }, [load]);

  const openLead = (l) => { setSelected(l); setStatus(l.status || "new"); setNote(l.note || ""); };
  const saveLead = async () => {
    try {
      await axios.patch(`${API}/admin/leads/${selected.id}`, { status, note }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lead updated"); setSelected(null); load();
    } catch { toast.error("Could not save"); }
  };
  const removeLead = async (l) => {
    if (!window.confirm(`Delete lead from ${l.name || l.email || "this contact"}?`)) return;
    try { await axios.delete(`${API}/admin/leads/${l.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lead deleted"); load();
    } catch { toast.error("Could not delete"); }
  };
  const exportCsv = () => {
    const rows = [
      ["Type", "Name", "Company", "Phone", "Email", "Location", "Monthly", "Territory", "Subject", "Message", "Status", "Created"],
      ...leads.map(l => [l.kind, l.name, l.company, l.phone, l.email, l.location, l.monthly_purchase, l.territory, l.subject, (l.message || "").replace(/\n/g, " "), l.status, l.created_at]),
    ];
    const csv = rows.map(r => r.map(v => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `sanatan-leads-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const KIND_LABEL = { retailer: "Retailer", distributor: "Distributor", contact: "Contact" };
  const STATUS_COLORS = { new: "#C89D3C", contacted: "#1D524D", won: "#1B2A49", lost: "#8A1A2B" };

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Inbox} label="Total Leads" value={summary.total || 0} testId="leads-total" />
        <StatCard icon={Inbox} label="Retailer" value={summary.retailer || 0} testId="leads-retailer" accent="#1D524D" />
        <StatCard icon={Inbox} label="Distributor" value={summary.distributor || 0} testId="leads-distributor" accent="#1B2A49" />
        <StatCard icon={Inbox} label="Contact" value={summary.contact || 0} testId="leads-contact" accent="#E06B22" />
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2 flex-wrap">
          {["", "retailer", "distributor", "contact"].map((k) => (
            <button key={k} onClick={() => setFilter(f => ({ ...f, kind: k }))} data-testid={`leads-filter-${k || "all"}`}
              className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2 border transition-colors ${filter.kind === k ? "bg-[#3B2A1A] text-white border-[#3B2A1A]" : "text-[#3B2A1A] border-[#3B2A1A]/20 hover:border-[#C89D3C]"}`}>
              {k ? KIND_LABEL[k] : "All"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
            className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] bg-transparent border border-[#3B2A1A]/20 px-3 py-2 focus:outline-none">
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <button onClick={exportCsv} data-testid="leads-export-btn"
            className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] border border-[#3B2A1A]/20 px-4 py-2 hover:border-[#C89D3C] inline-flex items-center gap-2">
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      <section className="bg-white border border-[#3B2A1A]/10 overflow-x-auto">
        {loading ? <div className="p-8 flex justify-center"><Loader2 size={20} className="animate-spin text-[#C89D3C]" /></div> :
          leads.length === 0 ? <p className="p-8 text-sm text-[#8A7969] font-light">No leads yet &mdash; as visitors submit the retailer, distributor or contact forms, they will show up here.</p> :
          <table className="min-w-full text-sm" data-testid="leads-table">
            <thead className="bg-[#F5EFE3]">
              <tr>
                {["Type", "Name / Company", "Phone", "Location", "Message / Territory", "Status", "Date", ""].map(h => (
                  <th key={h} className="text-left overline text-[10px] px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3B2A1A]/10">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-[#FBF6EC]/50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: STATUS_COLORS[l.status] || "#8A7969" }}>
                      {KIND_LABEL[l.kind] || l.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-serif-display text-base text-[#3B2A1A] leading-tight">{l.name || "—"}</p>
                    <p className="text-xs text-[#8A7969]">{l.company || l.email || ""}</p>
                  </td>
                  <td className="px-4 py-3 text-[#3B2A1A]">{l.phone || "—"}</td>
                  <td className="px-4 py-3 text-[#3B2A1A]">{l.location || "—"}</td>
                  <td className="px-4 py-3 text-[#5C4A3A] max-w-xs">
                    <span className="line-clamp-2">{l.subject || l.territory || l.monthly_purchase || l.message || "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: STATUS_COLORS[l.status] || "#8A7969" }}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8A7969] whitespace-nowrap">{l.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openLead(l)} data-testid={`lead-open-${l.id}`} className="text-xs text-[#3B2A1A] hover:text-[#C89D3C] font-semibold mr-3">Open</button>
                    <button onClick={() => removeLead(l)} className="text-xs text-[#8A1A2B] hover:opacity-70"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white border border-[#3B2A1A]/10 max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="overline">{KIND_LABEL[selected.kind]} Lead</p>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-1">{selected.name || selected.company || selected.email || "—"}</h3>
                <p className="text-xs text-[#8A7969] mt-1">Received {selected.created_at?.slice(0, 16)?.replace("T", " ")}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#8A7969] hover:text-[#3B2A1A]">×</button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[["Name", selected.name], ["Company", selected.company], ["Phone", selected.phone], ["Email", selected.email],
                ["Location", selected.location], ["Monthly Purchase", selected.monthly_purchase],
                ["Territory", selected.territory], ["Subject", selected.subject]].map(([k, v]) => v && (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-widest text-[#8A7969]">{k}</p>
                  <p className="text-[#3B2A1A] mt-1">{v}</p>
                </div>
              ))}
              {selected.message && (
                <div className="col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Message</p>
                  <p className="text-[#3B2A1A] mt-1 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}
            </div>
            <div className="mt-6 pt-6 border-t border-[#3B2A1A]/10 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8A7969]">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full text-sm text-[#3B2A1A] bg-transparent border-b border-[#3B2A1A]/20 py-2 focus:outline-none focus:border-[#C89D3C]">
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8A7969]">Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full text-sm text-[#3B2A1A] bg-transparent border-b border-[#3B2A1A]/20 py-2 focus:outline-none focus:border-[#C89D3C]" placeholder="e.g. Followed up on Tue" />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={saveLead} className="btn-primary" data-testid="lead-save-btn"><Check size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- ORDERS TAB ----------
function OrdersTab({ token, onExpire }) {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("new");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/admin/orders${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(data.orders); setCounts(data.counts);
    } catch (e) { if (e.response?.status === 401) onExpire(); }
    finally { setLoading(false); }
  }, [filter, token, onExpire]);
  useEffect(() => { load(); }, [load]);

  const open = (o) => { setSelected(o); setStatus(o.status || "new"); setNote(o.note || ""); };
  const save = async () => {
    try {
      await axios.patch(`${API}/admin/orders/${selected.id}`, { status, note }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Order updated"); setSelected(null); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.order_no}?`)) return;
    try { await axios.delete(`${API}/admin/orders/${o.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Order deleted"); load();
    } catch { toast.error("Could not delete"); }
  };

  const totalOf = (o) => (o.items || []).reduce((s, it) => {
    const n = parseFloat(String(it.price || "").replace(/[^\d.]/g, ""));
    return s + (isNaN(n) ? 0 : n * (it.qty || 1));
  }, 0);
  const STATUS_COLORS = { new: "#C89D3C", confirmed: "#1D524D", shipped: "#1B2A49", delivered: "#3B2A1A", cancelled: "#8A1A2B" };

  return (
    <div className="space-y-6" data-testid="orders-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={ShoppingBag} label="Total Orders" value={counts.total || 0} testId="orders-total" />
        <StatCard icon={ShoppingBag} label="New" value={counts.new || 0} accent="#C89D3C" />
        <StatCard icon={ShoppingBag} label="Confirmed" value={counts.confirmed || 0} accent="#1D524D" />
        <StatCard icon={ShoppingBag} label="Shipped" value={counts.shipped || 0} accent="#1B2A49" />
        <StatCard icon={ShoppingBag} label="Delivered" value={counts.delivered || 0} accent="#3B2A1A" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["", "new", "confirmed", "shipped", "delivered", "cancelled"].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2 border ${filter === s ? "bg-[#3B2A1A] text-white border-[#3B2A1A]" : "text-[#3B2A1A] border-[#3B2A1A]/20 hover:border-[#C89D3C]"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-[#C89D3C]" /></div> :
        orders.length === 0 ? <p className="text-sm text-[#8A7969] font-light bg-white p-8 border border-[#3B2A1A]/10">No orders yet.</p> :
        <div className="bg-white border border-[#3B2A1A]/10 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="orders-table">
            <thead className="bg-[#F5EFE3]">
              <tr>{["Order #", "Customer", "Phone", "Items", "Total", "Status", "Date", ""].map(h => <th key={h} className="text-left overline text-[10px] px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#3B2A1A]/10">
              {orders.map(o => (
                <tr key={o.id} className="hover:bg-[#FBF6EC]/50">
                  <td className="px-4 py-3 font-mono text-xs">{o.order_no}</td>
                  <td className="px-4 py-3">
                    <p className="font-serif-display text-base text-[#3B2A1A]">{o.name}</p>
                    <p className="text-xs text-[#8A7969]">{o.city}, {o.pincode}</p>
                  </td>
                  <td className="px-4 py-3 text-[#3B2A1A]">{o.phone}</td>
                  <td className="px-4 py-3 text-[#3B2A1A]">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="text-xs">{it.name} · {it.size} × {it.qty}</div>
                    ))}
                  </td>
                  <td className="px-4 py-3 font-serif-display text-lg text-[#C89D3C]">₹{totalOf(o).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: STATUS_COLORS[o.status] || "#8A7969" }}>{o.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8A7969] whitespace-nowrap">{o.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => open(o)} data-testid={`order-open-${o.id}`} className="text-xs text-[#3B2A1A] hover:text-[#C89D3C] font-semibold mr-3">Open</button>
                    <button onClick={() => remove(o)} className="text-xs text-[#8A1A2B] hover:opacity-70"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="overline">Order</p>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-1">{selected.order_no}</h3>
                <p className="text-xs text-[#8A7969] mt-1">{selected.created_at?.slice(0, 16)?.replace("T", " ")}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#8A7969] hover:text-[#3B2A1A]">×</button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Name</p><p className="text-[#3B2A1A] mt-1">{selected.name}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Phone</p><p className="text-[#3B2A1A] mt-1">{selected.phone}</p></div>
              {selected.email && <div><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Email</p><p className="text-[#3B2A1A] mt-1">{selected.email}</p></div>}
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Location</p><p className="text-[#3B2A1A] mt-1">{selected.city} · {selected.state} · {selected.pincode}</p></div>
              <div className="col-span-2"><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Address</p><p className="text-[#3B2A1A] mt-1 whitespace-pre-wrap">{selected.address}</p></div>
              {selected.notes && <div className="col-span-2"><p className="text-[10px] uppercase tracking-widest text-[#8A7969]">Notes</p><p className="text-[#3B2A1A] mt-1">{selected.notes}</p></div>}
            </div>
            <div className="border-t border-[#3B2A1A]/10 pt-4">
              <p className="overline mb-3">Items</p>
              {(selected.items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-2 border-b border-[#3B2A1A]/10">
                  <div><p className="text-[#3B2A1A]">{it.name}</p><p className="text-xs text-[#8A7969]">{it.size} × {it.qty}</p></div>
                  {it.price && <p className="text-[#C89D3C]">₹{it.price}</p>}
                </div>
              ))}
              <div className="flex justify-between mt-3 pt-3 border-t border-[#3B2A1A]/10">
                <p className="overline">Order Total</p>
                <p className="font-serif-display text-2xl text-[#3B2A1A]">₹{totalOf(selected).toLocaleString("en-IN")}</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-[#3B2A1A]/10 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8A7969]">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full text-sm text-[#3B2A1A] bg-transparent border-b border-[#3B2A1A]/20 py-2 focus:outline-none focus:border-[#C89D3C]">
                  {["new", "confirmed", "shipped", "delivered", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8A7969]">Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full text-sm text-[#3B2A1A] bg-transparent border-b border-[#3B2A1A]/20 py-2 focus:outline-none focus:border-[#C89D3C]" />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={save} className="btn-primary" data-testid="order-save-btn"><Check size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- PRODUCTS TAB ----------
function ProductsTab({ token, onExpire }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", slug: "", category: "pooja", tagline: "", short: "", image: "",
    sizes: "", packaging: "", price: "", variant_prices: "", benefits: "", how_to: "" };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/products`)
      .then(({ data }) => setProducts(data.products || []))
      .catch((e) => { if (e.response?.status === 401) onExpire(); })
      .finally(() => setLoading(false));
  }, [onExpire]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEdit(p);
    setForm({
      name: p.name, slug: p.slug, category: p.category,
      tagline: p.tagline || "", short: p.short, image: p.image || "",
      sizes: (p.sizes || []).join(", "), packaging: p.packaging || "",
      price: p.price || "",
      variant_prices: Object.entries(p.variant_prices || {}).map(([k, v]) => `${k}=${v}`).join(", "),
      benefits: (p.benefits || []).join("\n"),
      how_to: (p.how_to || []).join("\n"),
    });
    setShowForm(true);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const vp = {};
    form.variant_prices.split(",").map(s => s.trim()).filter(Boolean).forEach(pair => {
      const [k, v] = pair.split("=").map(x => (x || "").trim());
      if (k && v) vp[k] = v;
    });
    const payload = {
      name: form.name,
      slug: form.slug || undefined,
      category: form.category,
      tagline: form.tagline,
      short: form.short,
      image: form.image,
      sizes: form.sizes.split(",").map(s => s.trim()).filter(Boolean),
      packaging: form.packaging,
      price: form.price,
      variant_prices: vp,
      benefits: form.benefits.split("\n").map(s => s.trim()).filter(Boolean),
      how_to: form.how_to.split("\n").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (edit) await axios.patch(`${API}/admin/products/${edit.slug}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.post(`${API}/admin/products`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(edit ? "Product updated" : "Product created");
      setShowForm(false); load();
    } catch { toast.error("Could not save product"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This is permanent.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Product deleted"); load();
    } catch { toast.error("Could not delete"); }
  };

  const CATS = [
    { id: "pooja", label: "Pooja Essentials" },
    { id: "premium", label: "Premium Pooja Essentials" },
    { id: "sacred", label: "Sacred Essentials" },
    { id: "fragrance", label: "Fragrance & Rituals" },
    { id: "kits", label: "Pooja Kits" },
    { id: "learning", label: "Spiritual Learning" },
  ];

  return (
    <div className="space-y-6" data-testid="products-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline">Product Catalogue</p>
          <h2 className="font-serif-display text-3xl text-[#3B2A1A] mt-1">All Products</h2>
          <p className="text-sm text-[#8A7969] mt-1 font-light">Edit any product — name, price, description, sizes, image. Changes appear on the site after a refresh.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="product-new-btn"><Plus size={14} /> New Product</button>
      </div>

      {loading ? <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-[#C89D3C]" /></div> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-list">
          {products.map((p) => (
            <div key={p.slug} className="bg-white border border-[#3B2A1A]/10 p-4" data-testid={`product-row-${p.slug}`}>
              <div className="flex gap-4">
                {p.image && <img src={p.image} alt="" className="w-20 h-20 object-cover bg-[#F5EFE3] flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="overline text-[10px]">{(CATS.find(c => c.id === p.category) || {}).label || p.category}</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-1 leading-tight truncate">{p.name}</p>
                  {p.price && <p className="text-sm text-[#C89D3C] mt-1 font-semibold">{p.price}</p>}
                  <p className="text-xs text-[#8A7969] mt-1 line-clamp-1">{(p.sizes || []).join(" · ")}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#3B2A1A]/10 flex items-center gap-4 text-xs">
                <a href={`/products/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#3B2A1A] hover:text-[#C89D3C]">View</a>
                <button onClick={() => openEdit(p)} className="text-[#3B2A1A] hover:text-[#C89D3C] inline-flex items-center gap-1"><Edit size={12} /> Edit</button>
                <button onClick={() => remove(p)} className="text-[#8A1A2B] hover:opacity-70 ml-auto"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      }

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} className="bg-white max-w-3xl w-full p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-form">
            <p className="overline">{edit ? "Edit Product" : "New Product"}</p>
            <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-1 mb-6">{edit ? edit.name : "Add a new product"}</h3>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="product-input-name"
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]">
                    {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Tagline (short poetic line)</label>
                <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Short Description</label>
                <textarea required rows={3} value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })}
                  data-testid="product-input-short"
                  className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Sizes (comma-separated)</label>
                  <input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="25g, 50g, 100g"
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Packaging</label>
                  <input value={form.packaging} onChange={(e) => setForm({ ...form, packaging: e.target.value })} placeholder="Premium Jar"
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Base Price</label>
                  <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="₹ 199"
                    data-testid="product-input-price"
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Per-Size Prices (format: 25g=199, 50g=349, 100g=599)</label>
                <input value={form.variant_prices} onChange={(e) => setForm({ ...form, variant_prices: e.target.value })}
                  placeholder="25g=199, 50g=349, 100g=599"
                  data-testid="product-input-variant-prices"
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Product Image</label>
                <div className="mt-2 flex items-center gap-4">
                  {form.image && <img src={form.image} alt="" className="w-20 h-20 object-cover bg-[#F5EFE3] border border-[#3B2A1A]/10" />}
                  <div className="flex-1 space-y-2">
                    <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/products/pure-kapoor.jpg or full URL"
                      className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
                    <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] hover:text-[#C89D3C] cursor-pointer">
                      <Upload size={12} /> {uploading ? "Uploading..." : "Upload Image"}
                      <input type="file" accept="image/*" className="hidden" data-testid="product-image-upload"
                        onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Benefits (one per line)</label>
                <textarea rows={4} value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })}
                  className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">How to Use (one step per line)</label>
                <textarea rows={4} value={form.how_to} onChange={(e) => setForm({ ...form, how_to: e.target.value })}
                  className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="product-save-btn">{edit ? "Update" : "Create"} <Check size={14} /></button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- BLOG PUBLISHER TAB ----------
function BlogTab({ token, onExpire }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", category: "Rituals", image: "", read_time: "5 min read", body: "" });

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/blog`)
      .then(({ data }) => setPosts(data.posts))
      .catch((e) => { if (e.response?.status === 401) onExpire(); })
      .finally(() => setLoading(false));
  }, [onExpire]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm({ title: "", excerpt: "", category: "Rituals", image: "", read_time: "5 min read", body: "" }); setShowForm(true); };
  const openEdit = (p) => { setEdit(p); setForm({ title: p.title, excerpt: p.excerpt, category: p.category, image: p.image, read_time: p.read_time, body: p.body.join("\n\n") }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, body: form.body.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean) };
    try {
      if (edit) await axios.patch(`${API}/admin/blog/${edit.slug}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.post(`${API}/admin/blog`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(edit ? "Post updated" : "Post published"); setShowForm(false); load();
    } catch { toast.error("Could not save post"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await axios.delete(`${API}/admin/blog/${p.slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Post deleted"); load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6" data-testid="blog-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline">Blog Publisher</p>
          <h2 className="font-serif-display text-3xl text-[#3B2A1A] mt-1">Journal Posts</h2>
          <p className="text-sm text-[#8A7969] mt-1 font-light">Publish new articles instantly — they appear on /blog and are added to sitemap for Google.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="blog-new-btn"><Plus size={14} /> New Post</button>
      </div>

      {loading ? <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-[#C89D3C]" /></div> :
        posts.length === 0 ? (
          <div className="bg-white border border-[#3B2A1A]/10 p-10 text-center" data-testid="blog-empty">
            <p className="font-serif-display text-xl text-[#3B2A1A]">No admin posts yet.</p>
            <p className="text-sm text-[#8A7969] mt-2 font-light">The 14 built-in journal articles are always visible on /blog. Add your own here.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#3B2A1A]/10 divide-y divide-[#3B2A1A]/10" data-testid="blog-list">
            {posts.map((p) => (
              <div key={p.slug} className="p-5 flex items-center gap-5" data-testid={`blog-row-${p.slug}`}>
                {p.image && <img src={p.image} alt="" className="w-20 h-20 object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="overline text-[10px]">{p.category} · {p.read_time}</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-1 leading-tight truncate">{p.title}</p>
                  <p className="text-xs text-[#8A7969] mt-1 line-clamp-1">{p.excerpt}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#3B2A1A] hover:text-[#C89D3C]">View</a>
                  <button onClick={() => openEdit(p)} className="text-xs text-[#3B2A1A] hover:text-[#C89D3C] inline-flex items-center gap-1"><Edit size={12} /> Edit</button>
                  <button onClick={() => remove(p)} className="text-xs text-[#8A1A2B] hover:opacity-70"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      }

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={save} className="bg-white max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="blog-form">
            <p className="overline">{edit ? "Edit Post" : "New Post"}</p>
            <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-1 mb-6">{edit ? "Update your article" : "Publish a new article"}</h3>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Title</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="blog-input-title"
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Excerpt (short teaser)</label>
                <input required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  data-testid="blog-input-excerpt"
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]">
                    {["Rituals", "Tradition", "Practice", "Vastu", "Festivals"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Read Time</label>
                  <input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Image URL</label>
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/products/pure-kapoor.jpg"
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Body — separate paragraphs with a blank line</label>
                <textarea required rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                  data-testid="blog-input-body"
                  className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="blog-save-btn">{edit ? "Update" : "Publish"} <Check size={14} /></button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- ADMIN PANEL SHELL ----------
const AdminAnalytics = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("sd_admin_token"));
  const [tab, setTab] = useState("analytics");
  const onLogout = () => { sessionStorage.removeItem("sd_admin_token"); setToken(null); };

  if (!token) {
    return (
      <>
        <Helmet><title>Admin — Sanatan Dharma</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <LoginForm onSuccess={setToken} />
      </>
    );
  }

  const TABS = [
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "leads", label: "Leads", icon: Inbox },
    { id: "products", label: "Products", icon: Package },
    { id: "blog", label: "Blog", icon: FileText },
  ];

  return (
    <>
      <Helmet><title>Admin — Sanatan Dharma</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="min-h-screen bg-[#FBF6EC]" data-testid="admin-panel">
        <header className="bg-white border-b border-[#3B2A1A]/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
            <Logo />
            <button onClick={onLogout} data-testid="admin-logout-btn"
              className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] hover:text-[#C89D3C] inline-flex items-center gap-2">
              <LogOut size={14} /> Logout
            </button>
          </div>
          <div className="max-w-7xl mx-auto px-6 flex gap-2 border-t border-[#3B2A1A]/10">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`text-xs uppercase tracking-[0.18em] font-semibold px-5 py-4 border-b-2 inline-flex items-center gap-2 ${tab === t.id ? "border-[#C89D3C] text-[#C89D3C]" : "border-transparent text-[#3B2A1A] hover:text-[#C89D3C]"}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-10">
          {tab === "analytics" && <AnalyticsTab token={token} onExpire={onLogout} />}
          {tab === "orders" && <OrdersTab token={token} onExpire={onLogout} />}
          {tab === "leads" && <LeadsTab token={token} onExpire={onLogout} />}
          {tab === "products" && <ProductsTab token={token} onExpire={onLogout} />}
          {tab === "blog" && <BlogTab token={token} onExpire={onLogout} />}
        </main>
      </div>
    </>
  );
};

export default AdminAnalytics;
